import { RefreshCw, Sparkles } from 'lucide-react';
import PromotionClientPicker from './PromotionClientPicker';
import {
  SEGMENT_OPTIONS,
  TARGETING_MODES,
  OFFER_TYPE_OPTIONS,
  OFFER_TYPES,
  PROMO_CHANNEL_OPTIONS,
  buildPromotionPreview,
  generatePromoCode,
  getSegmentHelpText,
  needsFideliteWarning,
  normalizePromoCode,
} from '../../utils/promotionAdminUtils';

export default function PromotionForm({
  form,
  setForm,
  fieldErrors = {},
  commercants = [],
  commercantName = '',
  isCommercant = false,
  fixedCommercantId = '',
  fideliteConfig = null,
  isEdit = false,
}) {
  const isMontantFixe = form.typeOffre === OFFER_TYPES.MONTANT_FIXE;
  const preview = buildPromotionPreview(form, commercantName);
  const showFideliteWarning = needsFideliteWarning(form, fideliteConfig);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerateCode = () => {
    setField('codePromo', generatePromoCode(form.titre));
  };

  const toggleCanal = (value) => {
    const current = Array.isArray(form.canauxValides) ? form.canauxValides : [];
    if (current.includes(value)) {
      setField(
        'canauxValides',
        current.filter((c) => c !== value)
      );
    } else {
      setField('canauxValides', [...current, value]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Bloc 1 — L'offre */}
      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          1. L&apos;offre
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre de la promotion *
          </label>
          <input
            type="text"
            value={form.titre}
            onChange={(e) => setField('titre', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Ex. -20 % sur toute la carte"
          />
          {fieldErrors.titre && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.titre}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Texte visible par vos clients (5 caractères minimum)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Décrivez l&apos;offre telle que le client la comprendra.
          </p>
          {fieldErrors.description && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.description}</p>
          )}
        </div>

        {!isCommercant && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commerçant *
            </label>
            <select
              value={form.commercantId}
              onChange={(e) => setField('commercantId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Sélectionner un commerçant</option>
              {commercants.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
          </div>
        )}

        {isCommercant && fixedCommercantId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commerçant
            </label>
            <input
              type="text"
              value={commercantName || 'Mon commerce'}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type d&apos;offre
          </label>
          <select
            value={form.typeOffre || OFFER_TYPES.POURCENTAGE}
            onChange={(e) => setField('typeOffre', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            {OFFER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Livraison offerte, produit offert et autres types arriveront plus tard.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isMontantFixe ? 'Montant de réduction (€) *' : 'Réduction (%) *'}
            </label>
            <input
              type="number"
              min={isMontantFixe ? 0.01 : 1}
              max={isMontantFixe ? 10000 : 100}
              step={isMontantFixe ? 0.01 : 1}
              value={form.valeur}
              onChange={(e) => setField('valeur', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder={isMontantFixe ? '5' : '15'}
            />
            {fieldErrors.valeur && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.valeur}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code promo *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.codePromo}
                onChange={(e) => setField('codePromo', normalizePromoCode(e.target.value))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase"
                placeholder="ETE2026"
              />
              <button
                type="button"
                onClick={handleGenerateCode}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
              >
                <Sparkles className="h-4 w-4" />
                Générer
              </button>
            </div>
            {fieldErrors.codePromo && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.codePromo}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Montant minimum de panier (€)
          </label>
          <input
            type="number"
            min={0.01}
            step={0.01}
            value={form.montantMinimum}
            onChange={(e) => setField('montantMinimum', e.target.value)}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Vide = aucun minimum"
          />
          <p className="text-xs text-gray-500 mt-1">
            Le client doit atteindre ce montant de produits (hors livraison) pour utiliser le code.
          </p>
        </div>
      </section>

      {/* Bloc 2 — Qui */}
      <section className="rounded-xl border border-gray-200 bg-green-50/50 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          2. Qui en profite
        </h3>

        <div className="space-y-2">
          {[
            { value: TARGETING_MODES.ALL, label: 'Tous les clients' },
            { value: TARGETING_MODES.SEGMENT, label: 'Un segment (fidélité)' },
            { value: TARGETING_MODES.CLIENTS, label: 'Clients choisis' },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                form.targetingMode === opt.value
                  ? 'border-primary bg-white shadow-sm'
                  : 'border-gray-200 bg-white/80'
              }`}
            >
              <input
                type="radio"
                name="targetingMode"
                checked={form.targetingMode === opt.value}
                onChange={() => setField('targetingMode', opt.value)}
              />
              <span className="text-sm text-gray-900">{opt.label}</span>
            </label>
          ))}
        </div>

        {form.targetingMode === TARGETING_MODES.SEGMENT && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Segment
            </label>
            <select
              value={form.segmentClient}
              onChange={(e) => setField('segmentClient', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {SEGMENT_OPTIONS.filter((s) => s.value !== 'TOUS').map((s) => (
                <option key={s.value} value={s.value}>
                  {s.emoji} {s.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-2">
              {getSegmentHelpText(form.segmentClient, fideliteConfig)}
            </p>
            {showFideliteWarning && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                Programme fidélité inactif : ce segment ne pourra pas être appliqué
                correctement. Activez la fidélité ou choisissez « Tous les clients ».
              </p>
            )}
          </div>
        )}

        {form.targetingMode === TARGETING_MODES.CLIENTS && (
          <PromotionClientPicker
            commercantId={form.commercantId || fixedCommercantId}
            selectedClients={form.selectedClients || []}
            onChange={(selectedClients) => setField('selectedClients', selectedClients)}
          />
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Limite par client
          </label>
          <input
            type="number"
            min={1}
            value={form.limiteUtilisationParUser}
            onChange={(e) => setField('limiteUtilisationParUser', e.target.value)}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            placeholder="Vide = illimité"
          />
          <p className="text-xs text-gray-500 mt-1">
            Ex. 1 = chaque client ne peut utiliser le code qu&apos;une seule fois.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Canaux concernés
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PROMO_CHANNEL_OPTIONS.map((opt) => {
              const checked = (form.canauxValides || []).includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${
                    checked
                      ? 'border-primary bg-white shadow-sm'
                      : 'border-gray-200 bg-white/80'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCanal(opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Aucun canal sélectionné = valable sur tous les canaux (comportement
            historique).
          </p>
        </div>
      </section>

      {/* Bloc 3 — Quand */}
      <section className="rounded-xl border border-gray-200 bg-blue-50/40 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          3. Validité
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début *
            </label>
            <input
              type="date"
              value={form.dateDebut}
              onChange={(e) => setField('dateDebut', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin *
            </label>
            <input
              type="date"
              value={form.dateFin}
              onChange={(e) => setField('dateFin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            />
          </div>
        </div>
        <p className="text-xs text-gray-600">
          La promotion est valable jusqu&apos;à la fin du jour sélectionné (23h59).
          {isEdit
            ? ' Utilisez « Désactiver » dans la liste pour couper une promo avant terme.'
            : ' Les nouvelles promotions sont actives automatiquement dans cette période.'}
        </p>
      </section>

      {/* Aperçu */}
      <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Aperçu client
        </h3>
        <p className="text-sm font-medium text-amber-950">{preview.headline}</p>
        <ul className="mt-2 space-y-1">
          {preview.lines.map((line) => (
            <li key={line} className="text-sm text-amber-900">
              {line}
            </li>
          ))}
        </ul>
      </section>

      <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
        Promo automatique pour un seul client (sans code) ? Créez-la depuis la{' '}
        <strong>fiche client</strong> — section Promotions dédiées.
      </div>
    </div>
  );
}
